import { useState, useEffect, useRef } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  Bell,
  ChevronDown,
  LayoutDashboard,
  LogOut,
  Menu,
  Plus,
  Search,
  Settings2,
} from 'lucide-react';
import { useTranslation } from '@doccareplus/shared/lib';
import { cn } from '@doccareplus/shared/lib/utils';
import { ConfirmDialog, SearchSelect, ThemeSegmentedControl } from '@doccareplus/ui';
import { apiClientFactory } from '@doccareplus/api-client';
import { authApi } from '@doccareplus/kernel/infrastructure/api/auth.service';
import { useAppDispatch, useAppSelector } from '@doccareplus/kernel/application/store/hooks';
import {
  applySwitchedContext,
  logout,
  selectActiveContext,
  selectMemberships,
  selectUser,
} from '@doccareplus/kernel/application/store/slices/auth.slice';
import { toastApiError } from '@doccareplus/kernel/application';
import {
  getSubscriptionBannerInfo,
  hasClinicCapability,
  isSubscriptionLocked,
  SubscriptionPendingPaymentScreen,
  SubscriptionStatusBanner,
  useClinicAccessQuery,
} from '@features/clinic-access';
import { NotificationsDrawer, useNotificationLiveUpdates, useUnreadNotificationCountQuery } from '@features/notifications';
import { CommandPalette, type UICommand } from '@features/global-search';
import { RegisterPatientDrawer } from '../features/patients/ui/desktop/RegisterPatientDrawer';
import { BookAppointmentDrawer } from '../features/scheduling/ui/BookAppointmentDrawer';
import { todayDateInput } from '../features/scheduling/domain/appointmentView';
import { navSections, type ShellRoute } from './navModel';
import { useNavigationPageCommands, useVisibleQuickActions } from './useSearchCommands';
import { MobileShell } from './shells/MobileShell';

export function AppShell() {
  const isMobile = useIsMobileSurface();

  return isMobile ? <MobileShell /> : <DesktopShellContent />;
}

function getIsMobileSurface() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }

  return window.matchMedia('(max-width: 767px)').matches;
}

export function DesktopShellContent() {
  const { t } = useTranslation('common');
  const activeContext = useAppSelector(selectActiveContext);
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectUser);
  const [quickAction, setQuickAction] = useState<'patient' | 'appointment' | 'walkin' | null>(null);
  const memberships = useAppSelector(selectMemberships);
  const accessQuery = useClinicAccessQuery();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const visibleQuickActions = useVisibleQuickActions();
  const pageCommands = useNavigationPageCommands();

  // Drop routes the current role/plan can't reach, then drop any section left with no reachable
  // routes so we never render an empty section header.
  const visibleSections = navSections
    .map((section) => ({
      ...section,
      routes: section.routes.filter(
        (route) => !route.permissions || hasClinicCapability(accessQuery.data, route.permissions, route.feature),
      ),
    }))
    .filter((section) => section.routes.length > 0);
  const displayName = user ? `${user.firstName} ${user.lastName}`.trim() : t('workspace.userFallback');
  const activeMembership = memberships.find((membership) => membership.clinicId === activeContext?.clinicId) ?? memberships[0];
  const roleOptions = activeMembership?.roles?.length ? activeMembership.roles : activeMembership ? [activeMembership.role] : [];
  const activeRole = activeContext?.role ?? activeMembership?.role ?? null;
  // A membership always has at least one assigned role, so the selector must always show one. If the active
  // role no longer matches an assigned role (e.g. a stale/persisted value), fall back to the first assigned role.
  const matchedRole =
    roleOptions.find((r) => r.toLowerCase() === activeRole?.toLowerCase()) ?? roleOptions[0] ?? null;

  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(true);
  const [newMenuOpen, setNewMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [signOutConfirming, setSignOutConfirming] = useState(false);
  const location = useLocation();
  // The visit workspace renders its own full-height card with its own border/shadow/scroll regions —
  // `.page-wrap`'s outer gutter on top of that just doubles up as dead grey space around it.
  const fullBleedCanvas = /^\/visits\/[^/]+\/?$/.test(location.pathname);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const newMenuRef = useRef<HTMLDivElement>(null);
  const unreadNotifications = useUnreadNotificationCountQuery();
  useNotificationLiveUpdates();

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setProfileMenuOpen(false);
      }
      if (newMenuOpen && newMenuRef.current && !newMenuRef.current.contains(event.target as Node)) {
        setNewMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [newMenuOpen]);

  // ⌘K / Ctrl-K toggles the global command palette from anywhere in the workspace.
  useEffect(() => {
    function handleShortcut(event: KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setSearchOpen((open) => !open);
      }
    }
    window.addEventListener('keydown', handleShortcut);
    return () => window.removeEventListener('keydown', handleShortcut);
  }, []);

  // Global-search commands: navigable pages (capability-filtered) plus the same quick actions as the
  // "New" menu, each closing the palette and opening its drawer/route.
  const searchCommands: UICommand[] = [
    ...pageCommands,
    ...visibleQuickActions.map((action) => {
      const Icon = action.icon;
      return {
        id: `action:${(action as { actionType?: string }).actionType ?? (action as { route?: string }).route}`,
        category: 'actions' as const,
        title: t(action.labelKey),
        subtitle: t(action.descriptionKey),
        icon: <Icon className="h-[17px] w-[17px]" />,
        run: () => {
          setSearchOpen(false);
          const actionType = (action as { actionType?: 'patient' | 'appointment' | 'walkin' }).actionType;
          if (actionType) setQuickAction(actionType);
          else navigate((action as { route: string }).route);
        },
      };
    }),
  ];

  const applyRole = async (role: string) => {
    if (!activeMembership) return;

    try {
      const result = await authApi.switchContext(activeMembership.clinicId, role);
      // Apply the new per-request auth headers synchronously BEFORE the refetch below. The reactive
      // AuthSessionBridge effect also sets them, but it can lag the immediate clinic-access refetch — which
      // would then return the OLD role's permissions and leave the nav/dashboard unchanged.
      apiClientFactory.addHeader('X-Clinic-Id', result.clinicId);
      apiClientFactory.addHeader('X-Active-Role', result.role);
      await queryClient.cancelQueries({ queryKey: ['clinic-access'] });
      dispatch(applySwitchedContext(result));
      localStorage.setItem('doccareplus.current_clinic', result.clinicId);
      localStorage.setItem('doccareplus.current_role', result.role);
      queryClient.removeQueries({ queryKey: ['clinic-access'] });
      navigate(result.defaultRoute, { replace: true });
    } catch (error) {
      toastApiError(error, t('errors.switchRoleFailed'));
    }
  };

  const handleRoleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    void applyRole(event.target.value);
  };

  const handleClinicChange = async (clinicId: string) => {
    const targetMembership = memberships.find(m => m.clinicId === clinicId);
    if (!targetMembership) return;

    try {
      const result = await authApi.switchContext(targetMembership.clinicId, targetMembership.role);
      apiClientFactory.addHeader('X-Clinic-Id', result.clinicId);
      apiClientFactory.addHeader('X-Active-Role', result.role);
      await queryClient.cancelQueries({ queryKey: ['clinic-access'] });
      dispatch(applySwitchedContext(result));
      localStorage.setItem('doccareplus.current_clinic', result.clinicId);
      localStorage.setItem('doccareplus.current_role', result.role);
      queryClient.removeQueries({ queryKey: ['clinic-access'] });
      navigate(result.defaultRoute, { replace: true });
    } catch (error) {
      toastApiError(error, t('errors.switchClinicFailed'));
    }
  };

  // While the clinic is PendingPayment, the workspace is fully locked: no sidebar, no appbar nav, no
  // Outlet — otherwise a direct URL to e.g. /patients would still render the page underneath. Only
  // sign-out stays reachable so the clinic isn't stuck if the wrong account signed in.
  if (isSubscriptionLocked(accessQuery.data?.subscriptionStatus)) {
    return (
      <div className="min-h-screen bg-[var(--bg)] text-[var(--text)] flex flex-col">
        <header className="flex h-[60px] items-center justify-between border-b border-[var(--border)] bg-[var(--surface)] px-4 sm:px-[22px]">
          <div className="flex items-center gap-2 min-w-0">
            <img src="/short_logo.svg" alt="Logo" className="h-6 w-6 shrink-0" />
            <span className="truncate text-[13px] font-semibold text-[var(--text)]">
              {activeMembership?.clinicName ?? t('workspace.clinicName')}
            </span>
          </div>
          <button
            type="button"
            onClick={() => setSignOutConfirming(true)}
            className="flex shrink-0 items-center gap-1.5 rounded-[9px] px-3 py-2 text-[12.5px] font-semibold text-[var(--danger)] transition-colors hover:bg-[var(--danger-bg)] cursor-pointer"
          >
            <LogOut className="h-4 w-4" aria-hidden="true" />
            {t('actions.signOut')}
          </button>
        </header>
        <main className="page-wrap flex-1">
          <SubscriptionPendingPaymentScreen
            onCheckAgain={() => accessQuery.refetch()}
            isChecking={accessQuery.isFetching}
            planName={accessQuery.data?.planName}
            currency={accessQuery.data?.currency}
            finalPrice={accessQuery.data?.finalPrice}
          />
        </main>
        {signOutConfirming && (
          <ConfirmDialog
            title={t('actions.signOutConfirmTitle')}
            description={t('actions.signOutConfirmDescription')}
            confirmLabel={t('actions.signOut')}
            cancelLabel={t('actions.staySignedIn')}
            onConfirm={() => dispatch(logout())}
            onCancel={() => setSignOutConfirming(false)}
          />
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--bg)] text-[var(--text)]">
      {/* 2A. Backdrop Overlay for mobile sidebar */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 z-20 bg-black/40 backdrop-blur-sm transition-opacity duration-300 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* 2B. Sidebar Navigation */}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-30 w-[246px] border-r border-[var(--border)] bg-[var(--surface)] flex flex-col transition-transform duration-300 ease-in-out lg:translate-x-0",
          mobileMenuOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        )}
      >
        <div className="border-b border-[var(--border)] px-3 pb-3 pt-[14px]">
          <SearchSelect
            variant="rich"
            appearance="select"
            showDescriptionInTrigger
            searchable={memberships.length > 6}
            value={activeContext?.clinicId ?? activeMembership?.clinicId ?? null}
            onChange={handleClinicChange}
            placeholder={activeMembership?.clinicName ?? t('workspace.clinicName')}
            triggerAriaLabel={t('workspace.mobile.switchClinic')}
            searchPlaceholder={t('placeholder.search')}
            noResultsLabel={t('placeholder.noResults')}
            options={memberships.map((membership) => ({
              value: membership.clinicId,
              label: membership.clinicName,
              description: membership.city || membership.country || t('workspace.branch'),
              avatarText: getClinicInitials(membership.clinicName),
              leading: (
                <div className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-[9px] bg-gradient-to-tr from-[var(--primary)] to-[var(--brand-400)] shadow-sm">
                  <img src="/short_logo.svg" alt="Logo" className="h-4 w-4 brightness-0 invert" />
                </div>
              ),
            }))}
            footer={
              <button
                type="button"
                className="flex w-full items-center gap-2.5 rounded-[9px] px-2.5 py-2 text-left text-[12.5px] font-semibold text-[var(--text)] transition-colors hover:bg-[var(--surface-2)] cursor-pointer"
              >
                <span className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-[6px] border border-dashed border-[var(--border-strong)] text-[var(--text-2)]">
                  <Plus className="h-3 w-3" aria-hidden="true" />
                </span>
                <span>{t('actions.createClinic', { defaultValue: 'Create new clinic' })}</span>
              </button>
            }
          />
        </div>

        <nav className="flex-1 overflow-y-auto px-2.5 pb-1 pt-2.5" aria-label={t('workspace.navigationLabel')}>
          {visibleSections.map((section, index) => {
            const renderRoute = (route: ShellRoute, extraClass?: string) => (
              <NavLink
                key={route.path}
                to={route.path}
                end={route.end}
                className={({ isActive }) => cn('nav-item', extraClass, isActive && 'active')}
                onClick={() => setMobileMenuOpen(false)}
              >
                <span className="flex h-5 w-5 items-center justify-center" aria-hidden="true">
                  {route.icon}
                </span>
                <span>{t(route.labelKey)}</span>
              </NavLink>
            );

            if (section.collapsible) {
              // The parent lights up (accent text) whenever the active route is one of its children, so the
              // group still reads as "current" even while collapsed.
              const groupActive = section.routes.some((route) =>
                route.end ? location.pathname === route.path : location.pathname.startsWith(route.path),
              );
              return (
                <div key={section.titleKey ?? 'overview'} className={index > 0 ? 'mt-3' : undefined}>
                  <button
                    type="button"
                    aria-expanded={adminOpen}
                    onClick={() => setAdminOpen((open) => !open)}
                    className={cn('nav-item font-semibold', groupActive && 'text-[var(--primary)]')}
                  >
                    <span className="flex h-5 w-5 items-center justify-center" aria-hidden="true">
                      {section.groupIcon}
                    </span>
                    <span className="flex-1">{section.titleKey ? t(section.titleKey) : null}</span>
                    <ChevronDown
                      className={cn('h-[15px] w-[15px] shrink-0 transition-transform duration-200', adminOpen && 'rotate-180')}
                      aria-hidden="true"
                    />
                  </button>
                  {adminOpen && (
                    <div className="ml-[18px] mt-0.5 border-l border-[var(--border)] pl-1">
                      {section.routes.map((route) => renderRoute(route, 'pl-3'))}
                    </div>
                  )}
                </div>
              );
            }

            return (
              <div key={section.titleKey ?? 'overview'} className={index > 0 ? 'mt-3' : undefined}>
                {section.titleKey && (
                  <div className="px-2 pb-1 pt-1.5 text-[10.5px] font-semibold uppercase tracking-[0.06em] text-[var(--text-3)]">
                    {t(section.titleKey)}
                  </div>
                )}
                {section.routes.map((route) => renderRoute(route))}
              </div>
            );
          })}
        </nav>

        {/* Sidebar Bottom Selector Block */}
        {roleOptions.length > 0 && (
          <div className="border-t border-[var(--border)] p-2.5 bg-[var(--surface)]">
            <div className="flex flex-col gap-2">
              <label htmlFor="sidebar-role-select" className="text-[11px] font-bold text-[var(--text-3)] uppercase tracking-wider pl-0.5">
                {t('workspace.appbar.activeRole')}
              </label>

              {/* Hidden native select — kept for screen readers and Vitest coverage */}
              <select
                id="sidebar-role-select"
                aria-label={t('workspace.appbar.activeRole')}
                value={matchedRole ?? ''}
                onChange={handleRoleChange}
                className="sr-only"
              >
                {roleOptions.map((role) => (
                  <option key={role} value={role}>
                    {role}
                  </option>
                ))}
              </select>

              <SearchSelect
                variant="plain"
                searchable={false}
                placement="top"
                value={matchedRole}
                onChange={applyRole}
                options={roleOptions.map((role) => ({
                  value: role,
                  label: t(`role.${role.toLowerCase()}`, { defaultValue: role }),
                }))}
              />
            </div>
          </div>
        )}
      </aside>

      <div className="lg:pl-[246px]">
        <header className="sticky top-0 z-20 flex h-[60px] items-center gap-3 border-b border-[var(--border)] bg-[var(--surface)] px-4 sm:px-[22px]">

          {/* Left section: Mobile Hamburger */}
          <div className="flex flex-1 items-center gap-3 min-w-0">
            <button
              type="button"
              onClick={() => setMobileMenuOpen(true)}
              className="flex h-9 w-9 items-center justify-center rounded-[9px] border border-[var(--border)] bg-[var(--surface)] text-[var(--text-2)] hover:bg-[var(--surface-2)] focus:outline-none cursor-pointer lg:hidden"
              aria-label="Open navigation"
            >
              <Menu className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>

          {/* Center section: Search */}
          <div className="hidden lg:flex items-center justify-center">
            <button
              type="button"
              onClick={() => setSearchOpen(true)}
              aria-label={t('search.open')}
              aria-keyshortcuts="Meta+K Control+K"
              className="flex items-center gap-[9px] h-[36px] w-[280px] xl:w-[360px] rounded-[9px] border border-[var(--border)] bg-[var(--surface-2)] px-3 text-[13px] text-[var(--text-3)] cursor-pointer hover:border-[var(--border-strong)] transition-colors"
            >
              <Search className="h-[15px] w-[15px] shrink-0" aria-hidden="true" />
              <span className="flex-1 text-left truncate">{t('placeholder.search')}</span>
              <kbd className="hidden sm:inline-flex h-[22px] items-center justify-center font-mono text-[11px] bg-[var(--surface)] border border-[var(--border)] rounded-[5px] px-1.5 text-[var(--text-2)]">
                {navigator.platform.toUpperCase().indexOf('MAC') >= 0 ? '⌘K' : 'Ctrl K'}
              </kbd>
            </button>
          </div>

          {/* Right section: Appbar Actions */}
          <div className="flex flex-1 items-center justify-end gap-1.5 sm:gap-3">
            <div className="hidden lg:block relative" ref={newMenuRef}>
              {visibleQuickActions.length > 0 && (
                <button
                  type="button"
                  aria-haspopup="menu"
                  aria-expanded={newMenuOpen}
                  onClick={() => setNewMenuOpen(!newMenuOpen)}
                  className="flex h-9 items-center gap-1.5 rounded-[9px] bg-[var(--primary)] pl-2.5 pr-2 text-[12.5px] font-semibold text-[var(--primary-fg)] transition-colors hover:bg-[var(--primary-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary)] focus-visible:ring-offset-2"
                >
                  <Plus className="h-4 w-4" aria-hidden="true" />
                  {t('new')}
                  <ChevronDown className="h-3.5 w-3.5 opacity-80" aria-hidden="true" />
                </button>
              )}
              {newMenuOpen && visibleQuickActions.length > 0 && (
                <div
                  role="menu"
                  aria-orientation="vertical"
                  className="absolute right-0 top-full mt-2 w-48 rounded-[12px] border border-[var(--border)] bg-[var(--surface)] p-1.5 shadow-[var(--shadow-2)] origin-top-right z-50 animate-in fade-in slide-in-from-top-2"
                >
                  {visibleQuickActions.map((action) => {
                    const Icon = action.icon;
                    return (
                      <button
                        key={action.labelKey}
                        role="menuitem"
                        type="button"
                        onClick={() => {
                          setNewMenuOpen(false);
                          if (action.actionType) {
                            setQuickAction(action.actionType);
                          } else {
                            navigate(action.route, { state: action.state });
                          }
                        }}
                        className="flex items-center gap-2.5 px-2.5 py-2 rounded-[8px] text-[12.5px] font-semibold text-[var(--text)] hover:bg-[var(--surface-2)] transition-colors w-full text-left cursor-pointer"
                      >
                        <Icon className="h-4 w-4 text-[var(--text-2)]" aria-hidden="true" />
                        {t(action.labelKey)}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Mobile/tablet Search Icon (below the lg sidebar breakpoint, above the mobile shell) */}
            <button
              type="button"
              onClick={() => setSearchOpen(true)}
              aria-label={t('search.open')}
              className="lg:hidden flex h-[36px] w-[36px] shrink-0 items-center justify-center rounded-[9px] border border-[var(--border)] bg-[var(--surface)] text-[var(--text-2)] hover:bg-[var(--surface-2)] transition-colors cursor-pointer"
            >
              <Search className="h-[17px] w-[17px]" aria-hidden="true" />
            </button>

            <button
              type="button"
              aria-label={t('notifications')}
              onClick={() => setNotificationsOpen(true)}
              className="relative flex h-[36px] w-[36px] shrink-0 items-center justify-center rounded-[9px] border border-[var(--border)] bg-[var(--surface)] text-[var(--text-2)] hover:bg-[var(--surface-2)] hover:text-[var(--text)] transition-colors cursor-pointer"
            >
              <Bell className="h-[17px] w-[17px]" aria-hidden="true" />
              {(unreadNotifications.data?.count ?? 0) > 0 && <span className="absolute top-1.5 right-[7px] h-2 w-2 rounded-full bg-[var(--danger-solid)] border-2 border-[var(--surface)]" />}
            </button>

            <div className="hidden sm:block w-px h-[26px] bg-[var(--border)]" />

            {/* Profile Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                type="button"
                aria-label={t('workspace.nav.mySettings')}
                onClick={() => setProfileMenuOpen(!profileMenuOpen)}
                className={cn(
                  "flex items-center gap-2.5 h-[42px] p-1.5 pl-1.5 pr-3 rounded-[12px] border border-[var(--border)] bg-[var(--surface)] hover:bg-[var(--surface-2)] hover:border-[var(--border-strong)]/80 transition-all duration-200 focus:outline-none cursor-pointer hover:shadow-sm group",
                  profileMenuOpen && "bg-[var(--surface-2)] border-[var(--border-strong)] shadow-sm"
                )}
              >
                <div className="relative flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-[9px] bg-gradient-to-tr from-[var(--primary)] to-[var(--brand-400)] text-[12px] font-bold text-white shadow-sm ring-1 ring-black/5">
                  {user ? `${user.firstName[0]}${user.lastName[0]}`.toUpperCase() : 'U'}
                  <span className={cn(
                    "absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-[var(--success-solid)] border-2 border-[var(--surface)] shadow-sm transition-colors duration-200",
                    profileMenuOpen ? "border-[var(--surface-2)]" : "group-hover:border-[var(--surface-2)]"
                  )} />
                </div>
                <div className="text-left hidden md:flex flex-col justify-center min-w-0 pr-1 select-none">
                  <div className="truncate text-[13px] font-semibold text-[var(--text)] leading-tight group-hover:text-[var(--primary)] transition-colors">{displayName}</div>
                  {matchedRole && (
                    <div className="truncate text-[9px] font-semibold text-[var(--text-3)] leading-none mt-0.5 uppercase tracking-wider">Role: {t(`role.${matchedRole.toLowerCase()}`, { defaultValue: matchedRole })}</div>
                  )}
                </div>
                <ChevronDown className={cn("h-4 w-4 text-[var(--text-3)] transition-transform duration-300 ease-out group-hover:text-[var(--text-2)]", profileMenuOpen && "rotate-180")} aria-hidden="true" />
              </button>

              {/* Dropdown Menu */}
              {profileMenuOpen && (
                <div className="absolute right-0 mt-2.5 w-[240px] z-40 rounded-[16px] border border-[var(--border)] bg-[var(--surface)] p-2 shadow-[var(--shadow-3)] animate-[fadeIn_0.15s_ease-out] flex flex-col gap-1">

                  {/* User details card header */}
                  <div className="flex items-center gap-3 p-2 border-b border-[var(--border)] mb-1">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[11px] bg-gradient-to-tr from-[var(--primary)] to-[var(--brand-400)] text-[15px] font-bold text-white shadow-inner">
                      {user ? `${user.firstName[0]}${user.lastName[0]}`.toUpperCase() : 'U'}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-[13.5px] font-bold text-[var(--text)] leading-snug truncate">{displayName}</div>
                      {matchedRole && (
                        <span className="inline-flex mt-1 items-center px-1.5 py-0.5 rounded-[6px] text-[10px] font-semibold bg-[var(--primary-weak)] text-[var(--primary)] border border-[var(--primary)]/10 uppercase tracking-wider">
                          {t(`role.${matchedRole.toLowerCase()}`, { defaultValue: matchedRole })}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Clinic Meta Info */}
                  <div className="px-2 py-1 text-[11px] text-[var(--text-3)] font-medium truncate">
                    <span className="font-semibold text-[var(--text-2)]">{t('workspace.branch')}:</span> {activeMembership?.clinicName ?? t('workspace.clinicName')}
                  </div>

                  <div className="h-px bg-[var(--border)] my-1 mx-1" />

                  {/* Theme Switcher */}
                  <div className="px-2 py-1.5">
                    <div className="mb-2 text-[10px] font-bold uppercase tracking-wider text-[var(--text-3)]">
                      {t('workspace.appbar.theme', { defaultValue: 'Theme' })}
                    </div>
                    <ThemeSegmentedControl />
                  </div>

                  <div className="h-px bg-[var(--border)] my-1 mx-1" />

                  {/* Settings NavLink */}
                  <NavLink
                    to="/account/settings"
                    onClick={() => setProfileMenuOpen(false)}
                    className="flex items-center gap-2.5 px-2.5 py-2 rounded-[10px] text-[13px] font-semibold text-[var(--text-2)] hover:bg-[var(--surface-2)] hover:text-[var(--text)] transition-colors cursor-pointer group/item"
                  >
                    <Settings2 className="h-4 w-4 text-[var(--text-3)] group-hover/item:text-[var(--primary)] transition-colors" aria-hidden="true" />
                    <span>{t('workspace.nav.mySettings')}</span>
                  </NavLink>

                  {/* Sign Out Action */}
                  <button
                    type="button"
                    onClick={() => {
                      setProfileMenuOpen(false);
                      setSignOutConfirming(true);
                    }}
                    className="flex items-center gap-2.5 px-2.5 py-2 rounded-[10px] text-[13px] font-semibold text-[var(--danger)] hover:bg-[var(--danger-bg)] transition-colors w-full text-left cursor-pointer group/item"
                  >
                    <LogOut className="h-4 w-4 text-[var(--danger-solid)]" aria-hidden="true" />
                    <span>{t('actions.signOut')}</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <SubscriptionStatusBanner
          info={getSubscriptionBannerInfo(accessQuery.data)}
          canManageSubscription={hasClinicCapability(accessQuery.data, ['clinic.settings.manage'])}
        />

        <main className={fullBleedCanvas ? 'p-0' : 'page-wrap'}>
          <Outlet />
        </main>
      </div>
      <CommandPalette open={searchOpen} onClose={() => setSearchOpen(false)} commands={searchCommands} />
      {notificationsOpen && <NotificationsDrawer onClose={() => setNotificationsOpen(false)} />}
      {quickAction === 'patient' && (
        <RegisterPatientDrawer onClose={() => setQuickAction(null)} onRegistered={(id) => { setQuickAction(null); navigate(`/patients/${id}`); }} />
      )}
      {quickAction === 'appointment' && (
        <BookAppointmentDrawer isOpen={true} onClose={() => setQuickAction(null)} defaultDate={''} />
      )}
      {quickAction === 'walkin' && (
        <BookAppointmentDrawer isOpen={true} onClose={() => setQuickAction(null)} defaultMode="walkin" defaultDate={todayDateInput()} />
      )}
      {signOutConfirming && (
        <ConfirmDialog
          title={t('actions.signOutConfirmTitle')}
          description={t('actions.signOutConfirmDescription')}
          confirmLabel={t('actions.signOut')}
          cancelLabel={t('actions.staySignedIn')}
          onConfirm={() => dispatch(logout())}
          onCancel={() => setSignOutConfirming(false)}
        />
      )}
    </div>
  );
}

function useIsMobileSurface() {
  const [isMobile, setIsMobile] = useState(getIsMobileSurface);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }

    const query = window.matchMedia('(max-width: 767px)');
    const handleChange = () => setIsMobile(query.matches);

    query.addEventListener('change', handleChange);
    handleChange();

    return () => query.removeEventListener('change', handleChange);
  }, []);

  return isMobile;
}

export function EmptyWorkspacePage({ titleKey, descriptionKey }: { titleKey: string; descriptionKey: string }) {
  const { t } = useTranslation('common');

  return (
    <section className="flex min-h-[calc(100vh-150px)] items-center justify-center">
      <div className="w-full max-w-[720px] rounded-[14px] border border-[var(--border)] bg-[var(--surface)] p-8 text-center shadow-[var(--shadow-1)]">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-[12px] bg-[var(--primary-weak)] text-[var(--primary)]">
          <LayoutDashboard className="h-5 w-5" aria-hidden="true" />
        </div>
        <h2 className="mt-5 text-[22px] font-semibold text-[var(--text)]">{t(titleKey)}</h2>
        <p className="mx-auto mt-2 max-w-[520px] text-[13.5px] leading-relaxed text-[var(--text-2)]">
          {t(descriptionKey)}
        </p>
        <div className="mt-6 inline-flex rounded-full bg-[var(--surface-2)] px-3 py-1.5 text-[12px] font-semibold text-[var(--text-3)]">
          {t('status.comingSoon')}
        </div>
      </div>
    </section>
  );
}

function getClinicInitials(name: string) {
  const initials = name
    .split(' ')
    .filter(Boolean)
    .map((part) => part[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return initials || 'W';
}
